/**
 * NeedsYouBadge — the ONE standardised attention indicator (PO 2026-09-11).
 *
 * The red count pill that answers a single question: **what needs me right now?**
 * It is a LEADING INDICATOR, not decoration — the user follows the red dot and never has
 * to remember where the work is ("like those games where you follow the red notification
 * buttons and you can turn off your brain"). FTUE persona W7 / design-implication 8.
 *
 * Non-negotiable rules (rule 0c — honesty):
 *  - REAL counts only. The number is always derived from rows the caller actually fetched.
 *  - NEVER render a zero. An honest zero is silence, not a "0" badge — a visible 0 would
 *    teach the user that the dot is noise, destroying the affordance.
 *  - NEVER render a placeholder/optimistic number while loading. Absent data = no badge.
 *  - ONE component everywhere (entity switcher, sidebar, tabs, review queues) so the
 *    affordance is byte-identical wherever it appears.
 *
 * Accessibility: the count is exposed as text with an explicit label ("N need you"), so the
 * dot is never colour-only.
 */
import { cn } from "@/lib/utils";

export interface NeedsYouBadgeProps {
  /** Real outstanding count. <= 0 renders nothing (honest zero). */
  count?: number | null;
  /** `sm` = inline next to a label; `md` = standalone (tab / sidebar rail). */
  size?: "sm" | "md";
  /**
   * `red` (default) = something needs a DECISION. `neutral` = a total across containers
   * (P144 D-144-003's all-companies sum) or the quiet `Worth knowing` count: the same
   * number, without the alarm. Red is reserved for "this is on me now" — a second red
   * number beside the first would make both unreadable (P144 STEP 0, S1).
   */
  tone?: "red" | "neutral";
  /**
   * A leading word rendered inside the pill, e.g. `all` so the sum reads **all 7**.
   * It is what makes two numbers on one control unambiguous without a legend (S12/S13):
   * a bare `/7` asks the reader to work out which number is his, on every glance.
   */
  label?: string;
  /**
   * Where that word sits. `leading` (default) is the documented `all 7` form; `trailing` is the
   * portfolio form the PO named on 2026-09-28 — *"call it '60 total'"* — so the word states WHAT
   * the number is (a total across companies) instead of qualifying it. Both keep the rule the
   * prop above exists for: two numbers on one control must be tellable apart without a legend.
   */
  labelPosition?: "leading" | "trailing";
  /** Override the accessible/tooltip noun, e.g. "to review", "to decide". */
  noun?: string;
  /**
   * P144 D-144-019 — the quiet state: 50% intensity, same colour, SAME NUMBER.
   *
   * The PO's words: *"i rather would do fade to colour intensity to 50%, not fade completely
   * away"*. It must be derived from a recorded fact ("every item here has been opened"), never
   * from a clock and never from a page visit — a dot that dims on its own teaches the reader to
   * ignore it. It is a rendering of LOOKED-AT, never of DONE: the count is unchanged and the dot
   * still clears only on a decision (D-144-009).
   */
  dimmed?: boolean;
  className?: string;
}

export function NeedsYouBadge({
  count,
  size = "sm",
  tone = "red",
  label: lead,
  labelPosition = "leading",
  noun = "need you",
  dimmed = false,
  className,
}: NeedsYouBadgeProps) {
  // Honest zero: nothing to show, so show nothing.
  if (count == null || count <= 0) return null;

  const label = count > 99 ? "99+" : String(count);
  // The word keeps its place in speech too: `all 7 …` reads from the front, `60 total …` from the
  // back.
  const spoken =
    (labelPosition === "leading" && lead ? `${lead} ` : "") +
    `${label}` +
    (labelPosition === "trailing" && lead ? ` ${lead}` : "") +
    ` ${noun}` +
    (dimmed ? " — you have viewed all of these" : "");

  return (
    <span
      role="status"
      aria-label={spoken}
      title={spoken}
      data-dimmed={dimmed ? "true" : undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-bold leading-none transition-opacity",
        tone === "red"
          ? "bg-red-500 text-white"
          : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-100",
        // HALF THE INTENSITY, NOT HALF THE INFORMATION: opacity, never a lighter colour token and
        // never a different number. 50% keeps it legible on both themes and keeps the affordance
        // the same shape it has everywhere else.
        dimmed && "opacity-50",
        size === "sm" ? "min-w-[1.125rem] px-1.5 py-0.5 text-label-2xs" : "min-w-[1.375rem] px-2 py-1 text-xs",
        className,
      )}
    >
      {lead
        ? labelPosition === "trailing"
          ? `${label} ${lead}`
          : `${lead} ${label}`
        : label}
    </span>
  );
}

export default NeedsYouBadge;
